import { LegLocation } from '@api/Trinium/Endpoints/Leg/types';
import Yup from '@yup';
import { Dayjs } from 'dayjs';
import { parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';

export interface AppointmentFormik {
	scheduledDate: Dayjs | null;
	scheduledFromTime: Dayjs | null;
	scheduledToTime: Dayjs | null;
	actualDateTime: Dayjs | null;
	/** Validation flag indicating that Actual Pickup Time was received initially meaning it cannot be null'ed */
	hasActualTime: boolean;
	location: LegLocation | null;
}

/** Empty instance of AppointFormik */
export const emptyAppointmentFormik: AppointmentFormik = {
	scheduledDate: null,
	scheduledFromTime: null,
	scheduledToTime: null,
	actualDateTime: null,
	hasActualTime: false,
	location: null
};

/**
 * Validation Schema for the Appointment Form
 */
export const appointmentFormValidation = Yup.object<AppointmentFormik>().shape({
	scheduledDate: Yup.date()
		.nullable()
		.typeError('Invalid Date')
		.when('$isModified', {
			is: true,
			then: (me) => me.annualMinRange().annualMaxRange()
		})
		.when(['scheduledFromTime', 'scheduledToTime'], {
			is: (val: Dayjs | null) => val !== null,
			then: (me) => me.required('Scheduled Date required when entering Time')
		}),
	scheduledFromTime: Yup.date()
		.nullable()
		.typeError('Invalid Time')
		.when('scheduledToTime', {
			is: (val: Dayjs | null) => val !== null,
			then: (me) => me.required('Scheduled From Time required for To Range')
		}),
	scheduledToTime: Yup.date().nullable().typeError('Invalid Time').min(Yup.ref('scheduledFromTime'), 'To Time must be after From Time'),
	actualDateTime: Yup.date()
		.nullable()
		.typeError('Invalid Date Time')
		.when('$isModified', {
			is: true,
			then: (me) => me.annualMinRange().annualMaxRange()
		})
		.when('hasActualTime', {
			is: (val: boolean) => val,
			then: (me) => me.required('Actual appointment times cannot be removed')
		})
});

/**
 * Mapping function to wire unix epoch number from API into dayjs for form
 */
export function mapLegToAppointmentForm(
	scheduledFrom?: number,
	scheduledTo?: number,
	actual?: number,
	location?: LegLocation
): AppointmentFormik {
	return {
		scheduledDate: parseDayJs(scheduledFrom),
		scheduledFromTime: parseDayJs(scheduledFrom),
		scheduledToTime: parseDayJs(scheduledTo),
		actualDateTime: parseDayJs(actual),
		hasActualTime: Boolean(actual),
		location: location
			? {
					id: location.id,
					name: location.name,
					addr1: location.addr1,
					cityId: location.cityId,
					city: location.city,
					state: location.state,
					zip: location.zip,
					phone: location.phone,
					fax: location.fax,
					areaName: location.areaName,
					notes: location.notes,
					emailAddr: location.emailAddr,
					puNotes: location.puNotes,
					delvNotes: location.delvNotes
				}
			: null
	};
}

/**
 *
 */
export const pickupApptApiFieldMapping: Record<keyof AppointmentFormik, string> = {
	scheduledDate: 'schdPuFrom',
	scheduledFromTime: '',
	scheduledToTime: 'schdPuTo',
	actualDateTime: 'puDate',
	hasActualTime: '',
	location: ''
};

export const dlvApptApiFieldMapping: Record<keyof AppointmentFormik, string> = {
	scheduledDate: 'schdDelvFrom',
	scheduledFromTime: '',
	scheduledToTime: 'schdDelvTo',
	actualDateTime: 'delvDate',
	hasActualTime: '',
	location: ''
};
